'use es6';

import I18n from 'I18n';
import { Map as ImmutableMap, OrderedMap, fromJS } from 'immutable';
import { registerQuery } from 'data-fetching-client';
import { TIME_SERIES } from 'reporting-data/constants/configTypes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { RANGE_TYPES } from 'reporting-data/constants/dateRangeTypes';
import zeroFill from 'reporting-data/lib/zeroFill';
import { BUCKET_DATE_KEY_FORMAT, VIDEO_VIEWS_QUERY_FIELD_NAME } from '../constants';
import { fetchViewsReport, formatDateLabel, fromDateInt } from '../utils/unifiedEvents';
export const VIDEO_VIEWS_TIME_SERIES_QUERY = registerQuery({
  args: ['crmPageObjectCoordinate', 'contentId', 'startDateInt', 'endDateInt', 'frequency', 'videoObjectCoordinates'],
  fetcher: fetchViewsReport,
  fieldName: VIDEO_VIEWS_QUERY_FIELD_NAME
});

// necessary to use zeroFill util to fill in missing day/week/months
function buildReportConfig(reportData, {
  dateRange,
  frequency
}) {
  const seriesByDate = reportData.groupBy(bucket => bucket.get('dateInt'));
  const seriesByVideo = reportData.groupBy(bucket => bucket.get('breakdown'));
  dateRange = dateRange.toJS ? dateRange.toJS() : dateRange;
  const data = fromJS({
    dimension: {
      buckets: seriesByDate.map((buckets, dateInt) => {
        return ImmutableMap({
          key: fromDateInt(dateInt).format(BUCKET_DATE_KEY_FORMAT),
          metrics: OrderedMap(buckets.map(bucket => [bucket.get('breakdown'), ImmutableMap({
            SUM: bucket.get('views')
          })]))
        });
      }).toList(),
      property: 'dateProperty'
    }
  });
  const metrics = seriesByVideo.keySeq().toArray().map(property => ({
    metricTypes: [SUM],
    property
  }));
  const report = fromJS({
    configType: TIME_SERIES,
    filters: {
      dateRange: {
        property: 'dateProperty',
        value: {
          endDate: dateRange.endDate,
          rangeType: RANGE_TYPES.CUSTOM,
          startDate: dateRange.startDate
        }
      }
    },
    frequency,
    metrics
  });
  return {
    report,
    data
  };
}
export const getViewsTimeSeriesVisualization = (reportData, {
  dateRange,
  frequency
}) => {
  const seriesByVideo = reportData.groupBy(bucket => bucket.get('breakdown'));
  const videoIdToTitle = seriesByVideo.map(breakdowns => breakdowns.first().get('videoTitle'));
  const {
    report,
    data
  } = buildReportConfig(reportData, {
    dateRange,
    frequency
  });
  const filledReportData = zeroFill(report, data);
  const dataSeries = filledReportData.getIn(['dimension', 'buckets']).map(bucket => {
    return bucket.get('metrics').map((metric, property) => ({
      dateLabel: formatDateLabel(bucket.get('key'), {
        frequency
      }),
      videoTitle: videoIdToTitle.get(property),
      views: metric.get('SUM') || 0
    })).toList();
  }).flatten().toArray();
  const dataSeriesProperties = {
    dateLabel: {
      label: I18n.text('videoReports.videoViewsChart.viewDate')
    },
    views: {
      label: I18n.text('videoReports.videoViewsChart.views')
    }
  };
  return {
    data: {
      primary: {
        data: dataSeries,
        properties: dataSeriesProperties
      }
    },
    layers: [{
      color: {
        field: 'videoTitle'
      },
      dataset: 'primary',
      mark: {
        stacking: true,
        type: 'area'
      },
      x: {
        field: 'dateLabel',
        type: 'category'
      },
      y: {
        field: 'views',
        fill: 'true',
        type: 'linear'
      }
    }],
    type: 'XY'
  };
};